import React, { useState, useMemo, useEffect, useContext } from 'react';

import styled from 'styled-components';
import { ControlLabel, FormGroup } from 'react-bootstrap';

import SimpleSelect from '../../../utils/FormWidgets/SimpleSelect';
import { ACTIVITY_AVAILABLE_DAY_WEEKS } from '../../../../infrastructure/constants/activities';
import {
  validateBookingOptionFields,
  validatePriceFormat,
} from '../../../../infrastructure/utils/common';
import {
  BookingOption,
  BookingOptionSubType,
} from '../../../../types/Activity';
import BookingOptionContent from '../BookingOptionContent';
import { isAddonsSupported } from '../../../forms/EditPeekAndRedeamOrderForm/helpers';
import { ActivityContext } from '../../../../contexts/Activity';
import Error from '../../../../components/stateMessage/Error';

/* eslint-disable no-shadow */

const StyledWrapper = styled.div`
  margin-bottom: 20px;
  border-top: 1px solid #d9d9d9;
  padding: 10px;
  padding-top: 20px;
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

function getInitialValue(
  selectedBookingOption: BookingOption & number,
  initialFields: Partial<BookingOption>,
  isAddonsSupported?: boolean
) {
  let updateBookingOptions: Partial<BookingOption> = initialFields;
  if (selectedBookingOption !== -1) {
    updateBookingOptions = selectedBookingOption;
  }
  if (!isAddonsSupported) {
    updateBookingOptions.subType = BookingOptionSubType.bookingOption;
    updateBookingOptions.pricingModel = undefined;
  }
  return updateBookingOptions;
}

type Props = {
  addedBookingOptions: {
    label: string;
    title: string;
  }[];
  onAddBookingOption: (option: Partial<BookingOption>, id: string) => void;
  selectedBookingOption: BookingOption & number;
  setSelectedBookingOption: (option: unknown) => void;
  onDeleteBookingOption: (id) => void;
  isNotIntegrationEnabled?: boolean;
  isFareharborSource?: boolean;
};

const BookingOptionsCreateData = ({
  addedBookingOptions,
  onAddBookingOption,
  selectedBookingOption,
  setSelectedBookingOption,
  onDeleteBookingOption,
  isNotIntegrationEnabled,
  isFareharborSource,
}: Props) => {
  const activityContext = useContext(ActivityContext);
  const initialFields: Partial<BookingOption> = {
    hours: ACTIVITY_AVAILABLE_DAY_WEEKS,
    attendeesMinimum: 1,
    additionalCharges: [
      {
        name: 'Gratuity',
        percent: 0,
      },
      {
        name: 'Vendor Fees',
        percent: 0,
      },
    ],
  };

  const [fieldsValue, setFieldsValue] = useState<Partial<BookingOption>>(
    getInitialValue(
      selectedBookingOption,
      initialFields,
      isAddonsSupported(activityContext.source)
    )
  );

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const hideMessage = () => {
    setTimeout(() => {
      setErrorMessage(undefined);
    }, 5000);
  };

  const setDefaultState = () => {
    setFieldsValue(initialFields);
    setNoMaximumAttendees(false);
  };

  const [noMaximumAttendees, setNoMaximumAttendees] = useState(false);

  const isPriceValid = useMemo(() => validatePriceFormat(fieldsValue.price), [
    fieldsValue.price,
  ]);

  const onSave = event => {
    event.preventDefault();

    if (fieldsValue.additionalCharges) {
      if (isFareharborSource) {
        const hasAdditionalCharges = fieldsValue.additionalCharges.some(
          item => item.percent > 0
        );
        if (hasAdditionalCharges) {
          setErrorMessage(
            'A FareHarbor experience cannot have Gratuity or Vendor Fees specified'
          );
          hideMessage();
          return;
        }
      }
      fieldsValue.additionalCharges = fieldsValue.additionalCharges.map(
        charge => ({ ...charge, percent: Number(charge.percent) })
      );
    }

    if (selectedBookingOption === -1) setFieldsValue(initialFields);
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/prop-types
    const id = selectedBookingOption?.id;
    onAddBookingOption(fieldsValue, id);
    setDefaultState();
  };

  const onDelete = () => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/prop-types
    onDeleteBookingOption(selectedBookingOption?.id);
    setSelectedBookingOption(-1);
    setFieldsValue(initialFields);
  };

  const onChange = (key, value) => {
    setFieldsValue({
      ...fieldsValue,
      [key]: value,
    });
  };

  const onChangeAdditionalCharges = (name, value) => {
    const { additionalCharges = [] } = fieldsValue;
    const updateAdditionalCharges = additionalCharges;
    const idX = updateAdditionalCharges.findIndex(
      charge => charge.name === name
    );
    if (idX !== -1) {
      updateAdditionalCharges[idX].percent = value;
      setFieldsValue({
        ...fieldsValue,
        additionalCharges: updateAdditionalCharges,
      });
    }
  };

  useEffect(() => {
    if (selectedBookingOption === -1) {
      setFieldsValue(initialFields);
    } else {
      setFieldsValue(selectedBookingOption);
    }
  }, [JSON.stringify(selectedBookingOption)]);

  const isSaveAddDisabled = !validateBookingOptionFields(
    fieldsValue,
    noMaximumAttendees
  );
  return (
    <StyledWrapper>
      <FormGroup>
        <ControlLabel>Selected Booking Option</ControlLabel>
        <SimpleSelect
          // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
          // @ts-ignore
          onChange={value => setSelectedBookingOption(value)}
          value={selectedBookingOption}
          data={[
            { label: 'New Booking Option', value: -1 },
            ...(addedBookingOptions
              ? // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line react/prop-types
                addedBookingOptions.map(option => ({
                  label: option.title,
                  value: option,
                }))
              : []),
          ]}
        />
      </FormGroup>
      <BookingOptionContent
        isNotIntegrationEnabled={isNotIntegrationEnabled}
        option={fieldsValue}
        isPriceValid={isPriceValid}
        noMaximumAttendees={noMaximumAttendees}
        onChange={onChange}
        onChangeAdditionalCharges={onChangeAdditionalCharges}
        setFieldsValue={setFieldsValue}
        onFormSubmit={onSave}
        setNoMaximumAttendees={setNoMaximumAttendees}
      >
        <StyledButtonWrapper>
          <button
            type='button'
            className='btn btn-danger'
            style={{
              visibility: selectedBookingOption === -1 ? 'hidden' : 'visible',
            }}
            onClick={onDelete}
          >
            Delete
          </button>
          <button
            type='submit'
            className='btn btn-primary'
            disabled={isSaveAddDisabled}
          >
            {selectedBookingOption === -1
              ? 'Add Booking Option'
              : 'Update Booking Option'}
          </button>
        </StyledButtonWrapper>
      </BookingOptionContent>
      {!!errorMessage && <Error text={errorMessage} />}
    </StyledWrapper>
  );
};

export default BookingOptionsCreateData;
